import React, { PropsWithChildren, ReactNode, useEffect, useRef } from "react";
import styled from "styled-components";
import { CircleAlert } from "lucide-react";
import { hexToRGB } from "@shared/utils/misc";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";

interface NewLookFormAlertProps {
  title?: ReactNode;
  scrollIntoView?: boolean;
  className?: string;
}

const NewLookFormAlert = ({
  title,
  scrollIntoView,
  className,
  children,
}: PropsWithChildren<NewLookFormAlertProps>) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollIntoView) {
      ref.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [scrollIntoView, title, children]);

  return (
    <Alert ref={ref} role="alert" className={className}>
      <CircleAlert size={18} strokeWidth={NEW_LOOK_ICON_STROKE} aria-hidden />
      <Content>
        {title && <Title>{title}</Title>}
        {children && <Body>{children}</Body>}
      </Content>
    </Alert>
  );
};

export default NewLookFormAlert;

const Alert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 16px;
  border: 1px solid ${(p) => hexToRGB(p.theme.colors.red, 0.35)};
  border-radius: ${(p) => p.theme.radii.control + 2}px;
  background: ${(p) =>
    hexToRGB(p.theme.colors.red, p.theme.isDarkMode ? 0.12 : 0.06)};
  scroll-margin: 96px 0 16px;
  > svg {
    flex: none;
    margin-top: 1px;
    color: ${(p) => p.theme.colors.red};
  }
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const Title = styled.div`
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
`;
const Body = styled.div`
  color: ${(p) => p.theme.colors.primaryText};
  font-size: 13px;
  line-height: 19px;
  overflow-wrap: anywhere;
`;
